import React, { useContext, useEffect } from 'react'
import { Head } from '@notino/react-toolkit/exports/Head'
import { useIntl } from 'react-intl'

// utils
import { DEFAULT_LOCALE, getEnvironmentAndLocaleSpecificShopsConfig, getEnvironmentSpecificShopsConfig } from '../../../../appDefaults'
import { PAGE_LINKS } from '../../../../utils/helper'
import { AppContext } from '../../../../utils/appProvider'

// types
import { CmsSalonsMetadata, SalonsPagination } from '../../../../types/types'

type Props = {
	pagination: SalonsPagination
	metaData: CmsSalonsMetadata
}

const SalonsListPageHead = (props: Props) => {
	const { locale } = useIntl()
	const { productionEnvironment } = useContext(AppContext)

	const { pagination, metaData } = props

	const defaultShop = getEnvironmentAndLocaleSpecificShopsConfig(productionEnvironment, DEFAULT_LOCALE)
	const currentShop = getEnvironmentAndLocaleSpecificShopsConfig(productionEnvironment, locale)
	const shops = getEnvironmentSpecificShopsConfig(productionEnvironment)

	const currentPath = `${currentShop.url}${PAGE_LINKS['/salons/all'](currentShop.locale)}`

	useEffect(() => {
		document.title = metaData.metaTitle
	}, [metaData.metaTitle])

	// canonical href
	const canonicalHref = currentPath

	// alternate hrefs
	const alternateLinks = shops.map((shop) => {
		return {
			href: `${shop.url}${PAGE_LINKS['/salons/all'](shop.locale)}`,
			lang: shop.lang
		}
	})

	// x-default href
	const xDefaultHref = `${defaultShop.url}${PAGE_LINKS['/salons/all'](defaultShop.locale)}`

	// prev link
	const hasLessPages = pagination && pagination.page !== 1

	let prevLink: string | undefined

	if (hasLessPages) {
		prevLink = `${currentPath}?page=${pagination.page - 1}`
	}

	// next link
	const hasMorePages = pagination && pagination.totalPages && pagination.page !== pagination.totalPages

	let nextLink: string | undefined

	if (hasMorePages) {
		nextLink = `${currentPath}?page=${pagination.page + 1}`
	}

	// NOTE: defining children as array and using .flat() on it otherwise <Head> doesn't work properly
	// SIDENOTE: each item has to have unique 'key' because <Head/> iterates over the array
	const children = [
		// charset
		<meta key='charset' charSet='utf-8' />,

		// title
		<title key='title'>{metaData.metaTitle}</title>,

		// description
		<meta key='description' name='description' content={metaData.metaDescription} />,

		// keywords
		<meta key='keywords' name='keywords' content={metaData.metaKeywords} />,

		// Indexing
		<meta key='robots' name='robots' content={metaData.indexPage ? 'index, follow' : 'noindex, nofollow'} />,

		// canonical
		canonicalHref && <link key='canonical' rel='canonical' href={canonicalHref} />,
		// alternates
		alternateLinks.map((link, id) => {
			return <link key={`alternate-${id}`} rel='alternate' href={link?.href} hrefLang={link?.lang} />
		}),
		// x-default
		xDefaultHref && <link key='x-default' rel='alternate' href={xDefaultHref} hrefLang='x-default' />,

		// pagination SEO links
		prevLink && <link key='prev' rel='prev' href={prevLink} />,
		nextLink && <link key='next' rel='next' href={nextLink} />
	]
		.flat()
		.filter(Boolean)

	return <Head>{children}</Head>
}

export default SalonsListPageHead
